import { computeSectionProgress } from "./utils";

const easeOutQuart = (x: number) => 1 - Math.pow(1 - x, 4);

/** Progress-units advanced per second when auto-completing a phase. */
const AUTO_SPEED = 2.0;
/** Smoothing rate for the returned visibility (exponential damp, 1/s). */
const SMOOTH_LAMBDA = 10;

export type DecksVisState = {
  vEnter: number;
  vExit: number;
  /** Backward (scroll-up out of reviews) auto fade-out accumulator. */
  vReExit: number;
  /** True once the entrance has fully played (t reached the dwell band). */
  enterLatch: boolean;
  /** True once the decks were hidden by the forward exit and the scroll has
   *  come back down out of the exit band — arms the backward auto fade-out. */
  revealArmed: boolean;
  /** Smoothed visibility actually applied to scale/opacity. */
  smooth: number;
};

export const makeDecksVisState = (): DecksVisState => ({
  vEnter: 0,
  vExit: 0,
  vReExit: 0,
  enterLatch: false,
  revealArmed: false,
  smooth: 0,
});

export type DecksVisWindow = {
  /** t where entrance fade-in begins (camera parked). */
  entryStart: number;
  /** t where entrance fade-in ends; dwell begins. */
  entryEnd: number;
  /** t where exit fade-out begins (FBO cross-fade to reviews). */
  exitStart: number;
  /** t where exit fade-out ends. */
  exitEnd: number;
  /** Scroll-up only: t below which the decks auto fade-out when travelling
   *  back up out of reviews (decoupled from scroll, like the forward exit). */
  reverseExitStart: number;
};

/**
 * Advances decks visibility for one frame and returns smoothed visibility 0..1.
 *
 * No scroll-direction detection (drei's damped scroll yields equal offsets
 * between frames when paused, which a delta-sign test misreads as "forward").
 *
 *  - vExit (reviews side, no camera there): pure /decks model — auto-completes
 *    to 1 while in/after the exit band, snaps back to 0 below it. The
 *    appearance therefore always plays to full, regardless of where the
 *    scroll stops, exactly like the /decks page.
 *  - vEnter (entry side, camera moves at t<entryStart): auto-completes the
 *    forward fade-in (pre-latch); once shown, re-traversing the band
 *    scroll-couples so the disappearance reaches 0 exactly at entryStart, in
 *    lockstep with the camera.
 *  - vReExit (scroll-up out of reviews only): once the forward exit has
 *    hidden the decks and the scroll comes back down out of the exit band
 *    (revealArmed), dropping below reverseExitStart auto-fades them out on a
 *    timer — symmetric with the forward exit, so the backward pause no longer
 *    drags across the whole parked-camera stretch.
 *
 * The exponential smooth is owned here so the 3D boxes and the HTML overlay
 * (which both call this) stay perfectly in lockstep.
 */
export function updateDecksVis(
  s: DecksVisState,
  t: number,
  delta: number,
  w: DecksVisWindow,
): number {
  const autoSpeed = delta * AUTO_SPEED;
  const enterScroll = computeSectionProgress(t, w.entryStart, w.entryEnd);

  if (t < w.entryStart) {
    s.vEnter = 0;
    s.enterLatch = false;
    s.vReExit = 0;
    s.revealArmed = false;
  } else if (t >= w.entryEnd) {
    s.vEnter = Math.min(1, s.vEnter + autoSpeed);
    s.enterLatch = true;
  } else if (!s.enterLatch) {
    s.vEnter = Math.min(1, Math.max(enterScroll, s.vEnter + autoSpeed));
  } else {
    s.vEnter = enterScroll;
  }

  if (t >= w.exitStart) {
    // Back in the exit / reviews zone — the forward exit owns the hide again.
    s.vExit = Math.min(1, s.vExit + autoSpeed);
    s.revealArmed = false;
    s.vReExit = 0;
  } else {
    // Crossed back down out of a (near-)complete forward exit: the decks were
    // hidden in reviews and are now being re-revealed on the way up. Arm the
    // backward auto fade-out. The >0.5 guard avoids arming on a tiny forward
    // overshoot at the exitStart boundary.
    if (s.vExit > 0.5) s.revealArmed = true;
    s.vExit = 0;
  }

  // Backward auto fade-out: once armed (came up out of reviews), crossing
  // below reverseExitStart extinguishes the decks on a timer, decoupled from
  // scroll — symmetric with the forward exit at exitStart, instead of the old
  // scroll-coupled vanish stranded in the 6.8→6.7 band. Snaps back to 0 when
  // scrolled above reverseExitStart again (no partial stranding, mirrors
  // vExit). Untouched on the pure forward pass (revealArmed stays false).
  if (s.revealArmed && t < w.reverseExitStart) {
    s.vReExit = Math.min(1, s.vReExit + autoSpeed);
  } else if (t >= w.reverseExitStart) {
    s.vReExit = 0;
  }

  const target =
    easeOutQuart(s.vEnter) *
    (1 - easeOutQuart(s.vExit)) *
    (1 - easeOutQuart(s.vReExit));
  s.smooth += (target - s.smooth) * (1 - Math.exp(-SMOOTH_LAMBDA * delta));
  return s.smooth;
}
